// Editor Drawer Components for Medical Note Audit
// This file contains reusable components for the editor drawer functionality
// Matches the reference UI design from Refernce-UI.jsx

import React from "react";
import {
    Box,
    Typography,
    IconButton,
    Checkbox,
    FormControlLabel,
    Chip,
    useTheme,
} from "@mui/material";
import { Close, Clear, Edit, Check } from "@mui/icons-material";
import Button from "../../common/Button";

interface EditorDrawerProps {
    editorOpen: boolean;
    editingRequirement: string | null;
    nonCompliantRequirements: any[];
    colors: any;
    handleCancelEdit: () => void;
    progressNoteText?: string;
    aiSuggestions?: any;
    onSkipRequirement?: () => void;
    onEditSuggestion?: () => void;
    onAcceptChanges?: () => void;
}

export const EditorDrawer: React.FC<EditorDrawerProps> = ({
    editorOpen,
    editingRequirement,
    nonCompliantRequirements,
    colors,
    handleCancelEdit,
    progressNoteText = "",
    aiSuggestions = {},
    onSkipRequirement,
    onEditSuggestion,
    onAcceptChanges,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    if (!editorOpen) return null;

    const currentRequirement = nonCompliantRequirements.find((req) => req.id === editingRequirement);

    if (!currentRequirement) {
        console.warn("EditorDrawer: No requirement found for ID:", editingRequirement);
        return null;
    }

    const currentIndex = nonCompliantRequirements.findIndex((req) => req.id === editingRequirement);
    const suggestion = aiSuggestions[editingRequirement || ""];
    const noteLines = progressNoteText.split("\n");

    return (
        <Box
            sx={{
                position: "absolute",
                top: 0,
                right: 0,
                width: { xs: "100%", md: "60%" },
                height: "100%",
                backgroundColor: isDark ? "#1A1C23" : "#ffffff",
                borderLeft: `1px solid ${isDark ? "#374151" : "#e5e9ed"}`,
                boxShadow: isDark ? "-4px 0 20px rgba(0, 0, 0, 0.3)" : "-4px 0 20px rgba(15, 23, 42, 0.08)",
                display: "flex",
                flexDirection: "column",
                zIndex: 10,
            }}
        >
            {/* Header */}
            <Box sx={{ p: 2.5, borderBottom: "1px solid #374151" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                    <Typography variant="h6" fontWeight="600" color="#FFFFFF">
                        Edit Progress Note
                    </Typography>
                    <IconButton onClick={handleCancelEdit} sx={{ color: "#919EAB" }}>
                        <Close />
                    </IconButton>
                </Box>

                {/* Progress Indicator */}
                <Typography variant="caption" sx={{ color: "#919EAB", display: "block", mb: 2 }}>
                    Fixing Requirement {currentIndex + 1} of {nonCompliantRequirements.length}
                </Typography>

                {/* Requirement Context */}
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: "#222733",
                        border: "1px solid #374151",
                        mb: 1.5,
                    }}
                >
                    <Typography variant="caption" sx={{ color: "#919EAB", textTransform: "uppercase", fontSize: "0.65rem", display: "block", mb: 0.5 }}>
                        Addressing Requirement
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Chip
                            label={currentRequirement.id.replace(/_/g, "-")}
                            size="small"
                            sx={{
                                backgroundColor: "#2A2D38",
                                color: "#919EAB",
                                fontFamily: "monospace",
                                fontSize: "0.7rem",
                                height: "20px",
                            }}
                        />
                        <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                            {currentRequirement.name}
                        </Typography>
                    </Box>
                </Box>

                {/* Missing Elements */}
                {currentRequirement.missing_elements && currentRequirement.missing_elements.length > 0 && (
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: 1,
                            backgroundColor: "#222733",
                            border: "1px solid #374151",
                        }}
                    >
                        <Typography variant="caption" sx={{ color: "#919EAB", textTransform: "uppercase", fontSize: "0.65rem", display: "block", mb: 1 }}>
                            Missing Elements
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, m: 0 }}>
                            {currentRequirement.missing_elements.map((element: string, idx: number) => (
                                <Typography key={idx} component="li" variant="body2" sx={{ fontSize: "0.8125rem", mb: 0.5 }}>
                                    {element}
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Diff View Content */}
            <Box sx={{ flex: 1, overflow: "hidden", display: "flex", p: 2.5 }}>
                {/* Line Numbers */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        textAlign: "right",
                        pr: 1.5,
                        pt: 2,
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        color: "#919EAB",
                        borderRight: "1px solid #374151",
                        minWidth: "40px",
                        userSelect: "none",
                    }}
                >
                    {noteLines.map((_, index) => (
                        <Box key={index} sx={{ height: "22px", lineHeight: "22px" }}>
                            {index + 1}
                        </Box>
                    ))}
                </Box>

                {/* Diff Content */}
                <Box sx={{ flex: 1, overflow: "auto" }}>
                    <Box
                        sx={{
                            fontFamily: "monospace",
                            fontSize: "0.875rem",
                            lineHeight: "22px",
                            p: 2,
                        }}
                    >
                        {noteLines.map((line, index) => {
                            const lineNumber = index + 1;
                            const change = suggestion?.changes?.find(
                                (c: any) => (c.lineStart || c.line_start) === lineNumber
                            );

                            if (change && change.type === "modify") {
                                return (
                                    <Box key={index}>
                                        {/* Old line (deletion) */}
                                        <Box
                                            sx={{
                                                px: 1.5,
                                                py: 0.5,
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: 1,
                                                backgroundColor: "rgba(244, 67, 54, 0.15)",
                                                borderLeft: "3px solid #F44336",
                                            }}
                                        >
                                            <Typography component="span" sx={{ color: "#F44336", minWidth: "16px", fontFamily: "monospace" }}>
                                                -
                                            </Typography>
                                            <Typography component="span" sx={{ color: "#FFFFFF", fontFamily: "monospace", fontSize: "0.875rem" }}>
                                                {change.oldText || change.old_text}
                                            </Typography>
                                        </Box>
                                        {/* New line (addition) with action icons */}
                                        <Box
                                            sx={{
                                                px: 1.5,
                                                py: 0.5,
                                                position: "relative",
                                                backgroundColor: "rgba(16, 185, 129, 0.15)",
                                                borderLeft: "3px solid #10B981",
                                                pr: "120px",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                                <Typography component="span" sx={{ color: "#10B981", minWidth: "16px", fontFamily: "monospace" }}>
                                                    +
                                                </Typography>
                                                <Typography component="span" sx={{ color: "#FFFFFF", fontFamily: "monospace", fontSize: "0.875rem" }}>
                                                    {change.newText || change.new_text}
                                                </Typography>
                                            </Box>
                                            {/* Action Icons */}
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: "4px",
                                                    right: "8px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={onSkipRequirement}
                                                    sx={{
                                                        color: "#F44336",
                                                        p: 0.5,
                                                        "&:hover": { transform: "scale(1.1)", backgroundColor: "rgba(244, 67, 54, 0.1)" },
                                                    }}
                                                    title="Skip this requirement"
                                                >
                                                    <Clear sx={{ fontSize: "1rem" }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={onEditSuggestion}
                                                    sx={{
                                                        color: "#00B8D9",
                                                        p: 0.5,
                                                        "&:hover": { transform: "scale(1.1)", backgroundColor: "rgba(0, 184, 217, 0.1)" },
                                                    }}
                                                    title="Edit AI suggestion"
                                                >
                                                    <Edit sx={{ fontSize: "1rem" }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={onAcceptChanges}
                                                    sx={{
                                                        color: "#10B981",
                                                        p: 0.5,
                                                        "&:hover": { transform: "scale(1.1)", backgroundColor: "rgba(16, 185, 129, 0.1)" },
                                                    }}
                                                    title="Accept AI changes"
                                                >
                                                    <Check sx={{ fontSize: "1rem" }} />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            }

                            // Regular unchanged line
                            return (
                                <Box key={index} sx={{ px: 1.5, py: 0.5, color: "#919EAB" }}>
                                    <Typography component="span" sx={{ minWidth: "16px", display: "inline-block", fontFamily: "monospace", fontSize: "0.875rem" }}>
                                        {" "}
                                    </Typography>
                                    <Typography component="span" sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}>
                                        {line || "\u00A0"}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

interface VerificationModalProps {
    verificationOpen: boolean;
    verifiedRequirements: Set<string>;
    nonCompliantRequirements: any[];
    colors: any;
    handleVerificationChange: (requirementId: string) => void;
    handleCancelVerification: () => void;
    handleConfirmVerification: () => void;
}

export const VerificationModal: React.FC<VerificationModalProps> = ({
    verificationOpen,
    verifiedRequirements,
    nonCompliantRequirements,
    colors,
    handleVerificationChange,
    handleCancelVerification,
    handleConfirmVerification,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    if (!verificationOpen) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: 10001,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
            }}
            onClick={handleCancelVerification}
        >
            <Box
                sx={{
                    backgroundColor: isDark ? "#1A1C23" : "#ffffff",
                    borderRadius: 2,
                    boxShadow: isDark ? "0 10px 40px rgba(0, 0, 0, 0.5)" : "0 10px 40px rgba(15, 23, 42, 0.15)",
                    maxWidth: "600px",
                    width: "90%",
                    maxHeight: "80vh",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <Box sx={{ p: 3, borderBottom: "1px solid #374151" }}>
                    <Typography variant="h5" fontWeight="bold" color="#FFFFFF">
                        Verify Fixed Requirements
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#919EAB", mt: 1 }}>
                        Please confirm which requirements have been addressed in your edits
                    </Typography>
                </Box>

                {/* Modal Content */}
                <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
                    {nonCompliantRequirements.map((req) => (
                        <Box
                            key={req.id}
                            sx={{
                                mb: 2,
                                p: 2,
                                backgroundColor: "#222733",
                                border: "1px solid #374151",
                                borderRadius: 1,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={verifiedRequirements.has(req.id)}
                                        onChange={() => handleVerificationChange(req.id)}
                                        sx={{
                                            color: colors.grey[400],
                                            "&.Mui-checked": {
                                                color: colors.success[400],
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                                            <Chip
                                                label={req.id.replace(/_/g, "-")}
                                                size="small"
                                                sx={{
                                                    backgroundColor: "#2A2D38",
                                                    color: colors.primary[400],
                                                    fontFamily: "monospace",
                                                    fontWeight: "bold",
                                                    fontSize: "0.7rem",
                                                }}
                                            />
                                            <Chip
                                                label={req.severity}
                                                size="small"
                                                sx={{
                                                    backgroundColor:
                                                        req.severity === "CRITICAL"
                                                            ? colors.error[500]
                                                            : req.severity === "MAJOR"
                                                            ? colors.warning[500]
                                                            : colors.info[500],
                                                    color: "white",
                                                    fontWeight: "bold",
                                                    fontSize: "0.7rem",
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            fontWeight="600"
                                            color="#FFFFFF"
                                        >
                                            {req.name}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    ))}
                </Box>

                {/* Modal Footer */}
                <Box
                    sx={{
                        p: 3,
                        borderTop: "1px solid #374151",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="body2" sx={{ color: "#919EAB" }}>
                        {verifiedRequirements.size} of {nonCompliantRequirements.length}{" "}
                        requirements verified
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Button variant="outlined" onClick={handleCancelVerification}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleConfirmVerification}
                            disabled={verifiedRequirements.size === 0}
                        >
                            Confirm & Re-Audit
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

interface EditorOverlayProps {
    editorOpen: boolean;
    handleCancelEdit: () => void;
}

export const EditorOverlay: React.FC<EditorOverlayProps> = ({
    editorOpen,
    handleCancelEdit,
}) => {
    if (!editorOpen) return null;

    return (
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 9,
            }}
            onClick={handleCancelEdit}
        />
    );
};
